import { useContext, useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  Dropdown,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { Header } from "../../components";
import { InputText } from "iuvity-ui-components/dist/inputtext"
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useServices } from "../../hooks/useServices";
import { useMasters } from "../../hooks/useMasters";
import { PointsServiceSchema } from "../../validations/schemas";
import { AppContext } from "../../context/index";
import { ErrorMessage } from "@hookform/error-message";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { mastersFormatter } from "../../utils";
import { InputTextarea } from "iuvity-ui-components/dist/inputtextarea";

interface NewServicePointsProps {
  update?: boolean;
}

const formBlankValues = {
  name: "",
  pointServiceType: "",
  code: "",
  description: "",
};

export const NewServicePointsPage: React.FC<NewServicePointsProps> = ({
  update = false,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedPointsObject },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update
        ? {
            name: selectedPointsObject?.name,
            description: selectedPointsObject?.description,
            code: selectedPointsObject?.code,
            pointServiceType: selectedPointsObject?.pointServiceTypeId,
          }
        : formBlankValues,
  });

  const nameValues = useWatch({
    control: control,
    name: "name",
  });

  const descriptionValues = useWatch({
    control: control,
    name: "description",
  });

  const codeValues = useWatch({
    control: control,
    name: "code",
  });

  const pointServiceTypeValues = useWatch({
    control: control,
    name: "pointServiceType",
  });

  const { serviceAction } = useServices("ID");
  const { dataMaster } = useMasters("DATA")
  const formKeysOptions:any = {
    pointServiceType: mastersFormatter(dataMaster),
  };

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedPointsObject: null,
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionResult = await serviceAction(update ? "UPDATE" : "CREATE",
    update ? {...formValues, id: selectedPointsObject.id} : formValues);
    if (actionResult === "SUCCESS") {
      setAppState((prev) => ({
        ...prev,
        activeIndexPoints: 2,
    }));
      navigate("/pointsofattention");
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel", goHome?: boolean) => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`PointsOfAttention.${update ? "save" : "create"}ModalTitle`),
          content: t(`PointsOfAttention.${update ? "save" : "create"}ServiceModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Users.cancelModalTitle"),
          content: t("Users.cancelModalContent"),
          onConfirm: goHome ? () => navigate('/') : onReturn,
        }));
      default:
        return;
    }
  };

  // EFECTO PARA CONTROLAR CARACTERES NO PERMITIDOS SIN REGEX
  useEffect(()=>{
    watch((value)=>{
      if (
        value.name.includes('^')
        || value.name.includes('`')
        || value.name.includes('¨')
        || value.name.includes('~')
      ) {
        setValue('name', value.name.replace('^', '').replace('`', '').replace('¨', '').replace('~', ''))
      }
      if (
        value.code.includes('´')
        || value.code.includes('^')
        || value.code.includes('`')
        || value.code.includes('¨')
        || value.code.includes('~')
      ) {
        setValue('code', value.code.replace('´', '').replace('^', '').replace('`', '').replace('¨', '').replace('~', ''))
      }
      if (
        value.description.includes('^')
        || value.description.includes('`')
        || value.description.includes('¨')
        || value.description.includes('~')
      ) {
        setValue('description', value.description.replace('^', '').replace('`', '').replace('¨', '').replace('~', ''))
      }
    })
  },[watch])

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => isDirty ? handleModalProps("cancel") : onReturn()}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("PointsOfAttention.editServiceTitle") : t("PointsOfAttention.newServiceTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => isDirty ? handleModalProps("cancel", true) : navigate('/'),
          }}
          breadcrumbsItems={[
            {
              label: t("PointsOfAttention.pointsTitle"),
              command: () => isDirty ? handleModalProps("cancel") : onReturn(),
            },
            {
              label: t(`PointsOfAttention.${update ? 'edit' : 'create'}ServiceBreadcrumb`),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="flex align-items-center justify-content-center my-4"
        variant="tertiary"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
          <p style={{ marginBottom: 'var(--space-base)' }}>{
            t(`PointsOfAttention.${update ? 'edit' : 'create'}NewServiceSubtitle`)}
          </p>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(formBlankValues)
                  .map((key, index) => (
                    <div
                      key={`c-${key}`}
                      className="flex flex-column mb-3 px-6"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        name={key as any}
                        control={control}
                        rules={(PointsServiceSchema(t) as any)[key]}
                        render={({ field }) => {
                          field.onChange = (e) => {
                            setValue(field.name, e.target.value, REVALIDATE_FORM_PROPS)
                          };
                          const extraProps = {
                            keyfilter: (PointsServiceSchema(t) as any)[key]
                              ?.keyfilter,
                          };
                          return ['pointServiceType'].includes(key) ? (
                            // @ts-ignore
                            <div className='pointsofattention-fields' style={{ paddingLeft: '4rem' }}>
                              <Dropdown
                                key={key}
                                id={`dropdown-${key}`}
                                className={`dropdown mb-3`}
                                label={t(`PointsOfAttention.${key}Label`)}
                                placeholder={!update && t(`PointsOfAttention.${key}Placeholder`)}
                                options={formKeysOptions[key]}
                                showClear={false}
                                width="20rem"
                                {...field}
                              />
                            </div>
                          ) : ['description'].includes(key) ? (
                            <div className='pointsofattention-fields' style={{ paddingLeft: '4rem' }}>
                              <InputTextarea
                                key={key}
                                id={`input-label-${key}`}
                                label={t(`PointsOfAttention.${key}Label`)}
                                placeholder={t(`PointsOfAttention.${key}Placeholder`)}
                                maxLength={(PointsServiceSchema(t)as any)[key]?.maxLength?.value}
                                autoComplete="off"
                                className={
                                  // @ts-ignore
                                  errors[key]?.message ? "p-invalid" : "mb-4"
                                }
                                {...field}
                                {...extraProps}
                                cols={32}
                                autoResize
                              />
                            </div>
                          ): (
                            <div className='pointsofattention-fields' style={{ paddingLeft: '4rem' }}>
                              <InputText
                                key={key}
                                id={`input-label-${key}`}
                                label={t(`PointsOfAttention.${key}Label`)}
                                placeholder={t(`PointsOfAttention.${key}Placeholder`)}
                                maxLength={(PointsServiceSchema(t)as any)[key]?.maxLength?.value}
                                type="text"
                                autoComplete="off"
                                className={
                                  // @ts-ignore
                                  errors[key]?.message ? "p-invalid" : "mb-4"
                                }
                                {...field}
                                {...extraProps}
                              />
                            </div>
                          )
                          ;
                        }}
                      />
                      {!['pointServiceType'].includes(key) && (
                        <ErrorMessage
                          errors={errors}
                          name={key as any}
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              key={`small-${key}`}
                              className="p-error block"
                              style={{
                                marginTop: "0.5rem",
                                marginBottom: "1.5rem",
                                paddingLeft: '4rem',
                              }}
                            >
                              {message}
                            </small>
                          )}
                        />
                      )}
                    </div>
                  ))}
                </div>
            </div>
          </div>
        </div>
      </BoxFull>

      <BoxFull className="my-5 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("PointsOfAttention.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            variant="p-button"
            label={
              update
                ? t("PointsOfAttention.saveBtnLabel")
                : t("PointsOfAttention.saveBtnLabel")
            }
            disabled={
              !(
                nameValues?.length > 0 &&
                descriptionValues?.length > 0 &&
                codeValues?.length > 0 &&
                pointServiceTypeValues &&
                isValid &&
                (update ? isDirty : true)
               )
            }
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
